import '../../styles/fonts.css';
import React, { useEffect, useState } from 'react';
import { MessageList } from './MessageList';
import { MessageInput } from './MessageInput';
import { Chat } from '../../types/chat';
import { useAuth } from '../../context/AuthContext';
import { useChatsContext } from '../../context/ChatsContext';
import { useSocketContext } from '../../context/SocketContext';
import { useChatMessages } from '../../hooks/useChatMessages';
import { useChats } from '../../hooks/useChats';
import {
  SmallBroomIcon,
  TrashIcon,
  RandomIcon,
  KeyIcon,
  ChatsIcon,
  SearchIcon,
  QuestionIcon,
  ExitIcon,
  GroupIcon,
  QRCodeIcon,
} from '../../assets/SvgIcons';
import { LoadingScreen } from '../ui/LoadingScreen';
import { ConfirmModal, InputModal } from '../ui/Modal';
import { groupChatAPI } from '../../services/api';
import { GroupInfoModal } from '../ui/GroupInfoModal';

interface ChatWindowProps {
  chat: Chat;
  onBack: () => void;
  isDesktop?: boolean;
}

export const ChatWindow: React.FC<ChatWindowProps> = ({ chat, onBack, isDesktop = false }) => {
  const [groupInviteCode, setGroupInviteCode] = useState<string>('');
  const [typingUsers, setTypingUsers] = useState<string[]>([]);
  const [showChatMenu, setShowChatMenu] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showAppMenu, setShowAppMenu] = useState(false);
  const { user, logout } = useAuth();
  const { currentChat, deleteChat, clearChat, createChatByNumber } = useChats();
  const { loadChat, loadSystemChat, markChatAsRead } = useChatsContext();
  const socket = useSocketContext();
  const { messages /*, addTempMessage*/ } = useChatMessages(chat, chat?.isGroupChat ?? false);
  const [isTyping, setIsTyping] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [showRandomSearchLoading, setShowRandomSearchLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [showFindByNumberModal, setShowFindByNumberModal] = useState(false);

  const isSystemChat = chat.id === 0;
  const chatToDisplay = currentChat || chat;
  const [groupQRCodeData, setGroupQRCodeData] = useState<string>('');

  useEffect(() => {
    const fetchInviteCode = async () => {
      if (showDetailsModal && chat.isGroupChat) {
        try {
          const response = await groupChatAPI.getInviteCode(chat.id);
          const inviteCode = response.data.inviteCode;
          setGroupInviteCode(inviteCode);

          const currentOrigin = window.location.origin;
          const inviteLink = `${currentOrigin}/group?invite=${inviteCode}`;
          setGroupQRCodeData(inviteLink);
        } catch (error) {
          console.error('Ошибка загрузки invite code:', error);
          setGroupInviteCode(chat.companionNumber);
        }
      }
    };

    fetchInviteCode();
  }, [showDetailsModal, chat.id, chat.isGroupChat, chat.companionNumber]);

  useEffect(() => {
    if (chat.id === 0) {
      socket.joinSystemChat();
    } else {
      socket.joinChat(chat.id);
      markChatAsRead(chat.id);
    }

    return () => {
      if (chat.id === 0) {
        socket.leaveSystemChat();
      } else {
        socket.leaveChat(chat.id);
      }
    };
  }, [chat.id, markChatAsRead, socket]);

  useEffect(() => {
    if (!socket || !user) return;

    const handleChatDeleted = (data: { chatId: number; deletedBy: number; deletedByUserNumber?: string }) => {
      if (data.chatId === chat.id) {
        console.log('🗑️ ChatWindow: Текущий чат удален');

        if (data.deletedBy !== user.id && data.deletedByUserNumber) {
        }

        onBack();
      }
    };

    const handleChatCleared = (data: { chatId: number; clearedBy: number; clearedByUserNumber: string }) => {
      if (data.chatId === chat.id) {
        console.log('🧹 ChatWindow: Текущий чат очищен');

        if (data.clearedBy !== user.id) {
        }

        if (chat.id === 0) {
          loadSystemChat();
        } else {
          loadChat(chat.id);
        }
      }
    };

    const removeChatDeleted = socket.on('chat_deleted', handleChatDeleted);
    const removeChatCleared = socket.on('chat_cleared', handleChatCleared);

    return () => {
      removeChatDeleted();
      removeChatCleared();
    };
  }, [socket, user, chat.id, onBack, loadChat, loadSystemChat]);

  const handleRandomGroupChat = async () => {
    if (showRandomSearchLoading || !chat.isGroupChat) return;

    try {
      setShowRandomSearchLoading(true);

      const response = await groupChatAPI.findRandomUserInGroup(chat.id);
      const { chatId, companionNumber } = response.data;

      const newChat: Chat = {
        id: chatId,
        companionNumber,
        companionId: parseFloat(companionNumber),
        isGroupChat: false,
        unreadCount: 0,
        createdAt: new Date().toISOString(),
        isNew: true,
      };

      createChatByNumber(companionNumber);
      setTimeout(() => {
        setShowRandomSearchLoading(false);
        onBack();
      }, 1000);
    } catch (error: any) {
      setShowRandomSearchLoading(false);
      const errorMessage = error.response?.data?.message || 'Ошибка поиска случайного собеседника в группе';
      alert(errorMessage);
    }
  };

  const handleSendMessage = (content: string, tempId: string) => {
    if (!user) return;

    console.log('📤 Отправка сообщения:', {
      content,
      tempId,
      chatId: chat.id,
      isGroupChat: chat.isGroupChat,
    });

    /*addTempMessage(content, tempId);*/

    if (chat.id === 0) {
      socket.sendSystemMessage(content, tempId);
    } else if (chat.isGroupChat) {
      socket.sendGroupMessage(chat.id, content, tempId);
    } else {
      socket.sendMessage(chat.id, content, tempId);
    }
  };

  const handleLogoutConfirm = () => {
    logout();
    setShowLogoutConfirm(false);
  };

  const handleDeleteConfirm = () => {
    deleteChat(chat.id);
    setShowDeleteConfirm(false);
    onBack();
  };

  const handleClearConfirm = () => {
    clearChat(chat.id);
    setShowClearConfirm(false);
  };

  const handleFindByNumber = (userNumber: string) => {
    if (userNumber.trim()) {
      createChatByNumber(userNumber.trim());
      setShowFindByNumberModal(false);
    }
  };

  const handleStartTyping = () => {
    if (chat.id === 0) {
      socket.startSystemTyping();
    } else {
      socket.startTyping(chat.id);
    }
  };

  const handleStopTyping = () => {
    if (chat.id === 0) {
      socket.stopSystemTyping();
    } else {
      socket.stopTyping(chat.id);
    }
  };

  const handleRandomSearchComplete = () => {
    setShowRandomSearchLoading(false);
    socket.startSearching();
    onBack();
  };

  if (!user) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          fontFamily: 'monospace',
          fontSize: '14px',
        }}
      >
        Загрузка...
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        backgroundColor: '#e8e8e8',
        fontFamily: 'monospace',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      {!isDesktop && (
        <div
          style={{
            backgroundColor: '#000',
            color: '#fff',
            padding: '4px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            height: '30px',
            border: '2px solid #e8e8e8',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div
              onClick={onBack}
              style={{
                backgroundColor: '#000',
                color: '#FFF',
                fontFamily: 'Tiny5, monospace',
                fontSize: '19px',
                height: '26px',
                width: '26px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRight: '2px solid #e8e8e8',
                cursor: 'pointer',
              }}
            >
              Ё
            </div>
            {chat.isGroupChat ? (
              <div style={{ fontFamily: 'monospace', fontSize: '14px' }}>{chat.companionNumber}</div>
            ) : (
              <div style={{ fontFamily: 'monospace', fontSize: '14px' }}>
                Чат [{isSystemChat ? 'Тёрка' : chat.companionNumber}]
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: '8px' }}>
            {!isSystemChat && (
              <button
                onClick={() => {
                  setShowChatMenu(!showChatMenu);
                  setShowAppMenu(false);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#fff',
                  cursor: 'pointer',
                  fontFamily: 'monospace',
                  fontSize: '14px',
                }}
              >
                ЧАТ ▼
              </button>
            )}
            <button
              onClick={() => {
                setShowAppMenu(!showAppMenu);
                setShowChatMenu(false);
              }}
              style={{
                background: 'none',
                border: 'none',
                color: '#fff',
                cursor: 'pointer',
                fontFamily: 'monospace',
                fontSize: '14px',
              }}
            >
              МЕНЮ ▼
            </button>
          </div>
        </div>
      )}

      {/* Chat Menu */}
      {!isDesktop && showChatMenu && !isSystemChat && (
        <div
          style={{
            position: 'absolute',
            right: '48px',
            top: '28px',
            backgroundColor: '#fff',
            border: '2px solid #000',
            zIndex: 101,
            width: '192px',
          }}
        >
          {/* Категория "Этот чат" */}
          <div
            style={{
              padding: '8px 16px 4px 16px',
              fontFamily: 'Roboto Mono, Sans-serif',
              fontWeight: 500,
              fontSize: '12px',
              lineHeight: '100%',
              letterSpacing: '0%',
              color: '#666',
            }}
          >
            Этот чат
          </div>

          {chat.isGroupChat && (
            <button
              onClick={() => {
                setShowDetailsModal(true);
                setShowChatMenu(false);
              }}
              style={{
                width: '100%',
                padding: '8px 16px',
                background: 'transparent',
                border: 'none',
                textAlign: 'left',
                cursor: 'pointer',
                fontFamily: 'Roboto Mono, Sans-serif',
                fontWeight: 400,
                fontSize: '14px',
                lineHeight: '100%',
                letterSpacing: '0%',
                textTransform: 'uppercase',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <QRCodeIcon />
              Поделиться
            </button>
          )}

          <button
            onClick={() => {
              setShowClearConfirm(true);
              setShowChatMenu(false);
            }}
            style={{
              width: '100%',
              padding: '8px 16px',
              background: 'transparent',
              border: 'none',
              textAlign: 'left',
              cursor: 'pointer',
              fontFamily: 'Roboto Mono, Sans-serif',
              fontWeight: 400,
              fontSize: '14px',
              lineHeight: '100%',
              letterSpacing: '0%',
              textTransform: 'uppercase',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <SmallBroomIcon />
            Очистить
          </button>

          {
            !chat.isGroupChat &&
            <button
              onClick={() => {
                setShowDeleteConfirm(true);
                setShowChatMenu(false);
              }}
              style={{
                width: '100%',
                padding: '8px 16px',
                background: 'transparent',
                border: 'none',
                textAlign: 'left',
                cursor: 'pointer',
                fontFamily: 'Roboto Mono, Sans-serif',
                fontWeight: 400,
                fontSize: '14px',
                lineHeight: '100%',
                letterSpacing: '0%',
                textTransform: 'uppercase',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <TrashIcon />
              Удалить
            </button>
          }

          {/* Категория "Новый" */}
          <div
            style={{
              padding: '8px 16px 4px 16px',
              fontFamily: 'Roboto Mono, Sans-serif',
              fontWeight: 500,
              fontSize: '12px',
              lineHeight: '100%',
              letterSpacing: '0%',
              color: '#666',
              marginTop: '8px',
            }}
          >
            Новый
          </div>

          <button
            onClick={() => {
              setShowChatMenu(false);
              setShowRandomSearchLoading(true);
            }}
            style={{
              width: '100%',
              padding: '8px 16px',
              background: 'transparent',
              border: 'none',
              textAlign: 'left',
              cursor: 'pointer',
              fontFamily: 'Roboto Mono, Sans-serif',
              fontWeight: 400,
              fontSize: '14px',
              lineHeight: '100%',
              letterSpacing: '0%',
              textTransform: 'uppercase',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <RandomIcon />
            Случайный чат
          </button>

          <button
            onClick={() => {
              setShowFindByNumberModal(true);
              setShowChatMenu(false);
            }}
            style={{
              width: '100%',
              padding: '8px 16px',
              background: 'transparent',
              border: 'none',
              textAlign: 'left',
              cursor: 'pointer',
              fontFamily: 'Roboto Mono, Sans-serif',
              fontWeight: 400,
              fontSize: '14px',
              lineHeight: '100%',
              letterSpacing: '0%',
              textTransform: 'uppercase',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <SearchIcon />
            Найти по номеру
          </button>

          <button
            onClick={() => {
              onBack();
              setShowChatMenu(false);
            }}
            style={{
              width: '100%',
              padding: '8px 16px',
              background: 'transparent',
              border: 'none',
              textAlign: 'left',
              cursor: 'pointer',
              fontFamily: 'Roboto Mono, Sans-serif',
              fontWeight: 400,
              fontSize: '14px',
              lineHeight: '100%',
              letterSpacing: '0%',
              textTransform: 'uppercase',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <ChatsIcon />
            Все чаты
          </button>
        </div>
      )}

      {/* App Menu */}
      {!isDesktop && showAppMenu && (
        <div
          style={{
            position: 'absolute',
            right: 0,
            top: '28px',
            backgroundColor: '#fff',
            border: '2px solid #000',
            zIndex: 101,
            width: '192px',
          }}
        >
          <div
            style={{
              width: '100%',
              padding: '8px 16px',
              background: 'transparent',
              border: 'none',
              textAlign: 'left',
              fontFamily: 'Roboto Mono, Sans-serif',
              fontWeight: 400,
              fontSize: '14px',
              lineHeight: '100%',
              letterSpacing: '0%',
              textTransform: 'uppercase',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              color: '#000',
            }}
          >
            <KeyIcon />№ {user?.userNumber || 'Загрузка...'}
          </div>

          <button
            onClick={() => {
              alert('Это приложение для чата с анонимными пользователями!');
              setShowAppMenu(false);
            }}
            style={{
              width: '100%',
              padding: '8px 16px',
              background: 'transparent',
              border: 'none',
              textAlign: 'left',
              cursor: 'pointer',
              fontFamily: 'Roboto Mono, Sans-serif',
              fontWeight: 400,
              fontSize: '14px',
              lineHeight: '100%',
              letterSpacing: '0%',
              textTransform: 'uppercase',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <QuestionIcon />
            Что это?
          </button>

          <button
            onClick={() => {
              setShowLogoutConfirm(true);
              setShowAppMenu(false);
            }}
            style={{
              width: '100%',
              padding: '8px 16px',
              background: 'transparent',
              border: 'none',
              textAlign: 'left',
              cursor: 'pointer',
              fontFamily: 'Roboto Mono, Sans-serif',
              fontWeight: 400,
              fontSize: '14px',
              lineHeight: '100%',
              letterSpacing: '0%',
              textTransform: 'uppercase',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <ExitIcon />
            Выйти
          </button>
        </div>
      )}

      {/* Messages */}
      <div
        style={{
          flex: 1,
          overflow: 'hidden',
          padding: isDesktop ? '16px 10%' : '8px',
          backgroundColor: '#e8e8e8',
        }}
      >
        <MessageList
          messages={messages}
          currentUserNumber={user.userNumber}
          companionNumber={
            chat.isGroupChat
              ? chat.companionNumber
              : chat.companionNumber !== 'Тёрка'
                ? '[' + chat.companionNumber + ']'
                : 'тёркой'
          }
          isGroupChat={chat.isGroupChat}
        />
      </div>

      {chat.isGroupChat && (
        <div
          style={{
            position: 'absolute',
            right: 0,
            bottom: 80,
          }}
        >
          <div
            onClick={handleRandomGroupChat}
            style={{
              width: '82px',
              height: '82px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontFamily: 'Tiny5, monospace',
              fontSize: '20px',
              cursor: showRandomSearchLoading ? 'not-allowed' : 'pointer',
              opacity: showRandomSearchLoading ? 0.5 : 1,
            }}
          >
            <RandomIcon />
          </div>
        </div>
      )}

      {/* Input */}
      <MessageInput
        onSendMessage={handleSendMessage}
        onStartTyping={handleStartTyping}
        onStopTyping={handleStopTyping}
        currentUserNumber={user.userNumber}
        isDesktop={isDesktop}
      />

      {/* Подтверждение удаления чата */}
      <ConfirmModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDeleteConfirm}
        title='УДАЛЕНИЕ ЧАТА'
        message={`Удалить чат с ${chat.companionNumber}? Это действие нельзя отменить.`}
        confirmText='УДАЛИТЬ'
        confirmColor={'#DA3300'}
        cancelText='ОТМЕНА'
      />

      <GroupInfoModal
        showOnly
        isOpen={showDetailsModal}
        onClose={() => setShowDetailsModal(false)}
        onConfirm={() => {}}
        groupNumber={chat.companionNumber}
        inviteCode={groupInviteCode}
        qrCodeData={groupQRCodeData} // ✅ Добавили передачу qrCodeData
      />

      {/* Подтверждение очистки чата */}
      <ConfirmModal
        isOpen={showClearConfirm}
        onClose={() => setShowClearConfirm(false)}
        onConfirm={handleClearConfirm}
        title='ОЧИСТКА ЧАТА'
        message='Очистить чат от ваших сообщений? Это действие нельзя отменить.'
        confirmText='ОЧИСТИТЬ'
        confirmColor={'#DA3300'}
        cancelText='ОТМЕНА'
      />

      {/* Поиск по номеру */}
      <InputModal
        isOpen={showFindByNumberModal}
        onClose={() => setShowFindByNumberModal(false)}
        onConfirm={handleFindByNumber}
        title='ВВЕДИТЕ НОМЕР ПОЛЬЗОВАТЕЛЯ'
        placeholder='00000000'
        maxLength={8}
        inputType='text'
        confirmText='НАЙТИ'
        cancelText='ОТМЕНА'
      />

      {/* Подтверждение выхода */}
      <ConfirmModal
        isOpen={showLogoutConfirm}
        onClose={() => setShowLogoutConfirm(false)}
        onConfirm={handleLogoutConfirm}
        title='ПОДТВЕРЖДЕНИЕ ВЫХОДА'
        message='Вы уверены, что хотите выйти из аккаунта?'
        confirmText='ВЫЙТИ'
        confirmColor={'#DA3300'}
        cancelText='ОТМЕНА'
      />

      {/* LoadingScreen остается в конце */}
      <LoadingScreen
        isVisible={showRandomSearchLoading}
        onComplete={handleRandomSearchComplete}
        loadingText='Поиск случайного собеседника...'
        minDuration={5000}
      />

      <LoadingScreen
        isVisible={showRandomSearchLoading}
        onComplete={() => setShowRandomSearchLoading(false)}
        loadingText='Поиск случайного собеседника в группе...'
        minDuration={5000}
      />
    </div>
  );
};
